/*
 * The C64 game's text format (`display_text_string`, $E42A): a byte below $10 sets the colour, $10
 * ends the string, $1F col,row moves the cursor, and any other byte draws the character byte - $20
 * of the game's own charset (whose letters sit where ASCII's do, with ! , ' at $5C $5E $5F).
 */

/** One run of text as the C64 draws it: its column and row on the 40x25 text screen, its colour. */
export interface C64TextLine {
  col: number;
  row: number;
  colour: number;
  text: string;
}

const END = 0x10;
const AT = 0x1f;
const INITIAL_COLOUR = 1;

const PUNCTUATION: Readonly<Record<number, string>> = { 0x40: ' ', 0x5c: '!', 0x5e: ',', 0x5f: "'" };

/** The character a byte of the game's text format draws; '?' for codes with no mapping. */
export function c64Char(code: number): string {
  const p = PUNCTUATION[code];
  if (p !== undefined) return p;
  const letter = code >= 0x41 && code <= 0x5a;
  const digit = code >= 0x30 && code <= 0x39;
  return letter || digit ? String.fromCharCode(code) : '?';
}

/** Decode a string in the game's text format into its lines. */
export function decodeC64Text(bytes: readonly number[]): C64TextLine[] {
  const lines: C64TextLine[] = [];
  let line: C64TextLine | null = null;
  let colour = INITIAL_COLOUR;
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b === undefined || b === END) break;
    if (b < END) {
      colour = b;
    } else if (b === AT) {
      line = { col: bytes[i + 1] ?? 0, row: bytes[i + 2] ?? 0, colour, text: '' };
      lines.push(line);
      i += 2;
    } else {
      if (!line) {
        line = { col: 0, row: 0, colour, text: '' };
        lines.push(line);
      }
      // A line takes the colour its first character is drawn in.
      if (line.text === '') line.colour = colour;
      line.text += c64Char(b);
    }
  }
  return lines;
}
